import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import JSZip from "jszip";
import { toStaticProxyUrl } from "@/utils/to-static-proxy-url";
import axios from "axios";

export interface DownloadTask {
  id: string;
  filename: string;
  total: number;
  loaded: number;
  percent: number;
  done: boolean;
  error?: string;
  imageUrl?: string;
  blobUrl?: string;
  compressionProgress?: number; // 新增压缩进度属性
}

export interface VideoFile {
  url: string;
  filename: string;
  imageUrl?: string;
}

export interface FileItem {
  url: string;
  filename: string;
  imageUrl?: string;
}

type FolderFileMap = Record<string, FileItem[]>;

// --- 文件类型检测工具函数 ---
const getFileTypeFromUrl = (url: string): string | null => {
  try {
    // 从URL中提取文件扩展名
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const lastDotIndex = pathname.lastIndexOf('.');
    if (lastDotIndex === -1) return null;
    return pathname.substring(lastDotIndex).toLowerCase();
  } catch {
    return null;
  }
};

// 判断是否为 doc 文件
const isDocFileFromBuffer = (
  buffer: Uint8Array,
  filename: string
): boolean => {
  if (buffer.length < 8) return false;

  // OLE 文件头
  const isOLE =
    buffer[0] === 0xD0 &&
    buffer[1] === 0xCF &&
    buffer[2] === 0x11 &&
    buffer[3] === 0xE0 &&
    buffer[4] === 0xA1 &&
    buffer[5] === 0xB1 &&
    buffer[6] === 0x1A &&
    buffer[7] === 0xE1;

  if (!isOLE) return false;

  // 文件名必须明确是 .doc
  const ext = getFileExtensionFromFilename(filename);
  return ext === '.doc';
};

const getFileTypeFromBuffer = (buffer: Uint8Array, filename?: string): string | null => {
  // 检查文件头部字节来判断真实文件类型
  if (buffer.length < 4) return null;

  const bytes = Array.from(buffer.slice(0, 12));

  // 图片格式检测
  if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
    return '.jpg';
  }
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
    return '.png';
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return '.gif';
  }
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
    return '.webp';
  }

  // 视频格式检测
  if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x00 &&
    (bytes[3] === 0x18 || bytes[3] === 0x20) &&
    bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    return '.mp4';
  }
  if (bytes[0] === 0x1A && bytes[1] === 0x45 && bytes[2] === 0xDF && bytes[3] === 0xA3) {
    return '.webm';
  }
  if (bytes[0] === 0x46 && bytes[1] === 0x4C && bytes[2] === 0x56) {
    return '.flv';
  }

  // 音频格式检测
  if (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0) {
    return '.mp3';
  }
  if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return '.ogg';
  }
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x41 && bytes[10] === 0x56 && bytes[11] === 0x45) {
    return '.wav';
  }

  // 文档格式检测
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return '.pdf';
  }

  // 将 buffer 转为字符串以便后续检索特征码（仅取前 2000 字节以保证性能）
  const headerString = Array.from(buffer.slice(0, 2000))
    .map(b => String.fromCharCode(b))
    .join('');

  if (bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04) {
    // 深入检查是否为 Office 文档
    if (headerString.includes('word/')) return '.docx';
    if (headerString.includes('xl/')) return '.xlsx';
    if (headerString.includes('ppt/')) return '.pptx';
  }

  // 检测docx
  if (bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04 &&
    bytes[4] === 0x14 && bytes[5] === 0x00 && bytes[6] === 0x06 && bytes[7] === 0x00) {
    return '.docx';
  }

  // 检测doc
  if (isDocFileFromBuffer(buffer, filename)) {
    return '.doc';
  }

  if (bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04) {
    return '.zip';
  }

  return null;
};

const getFileExtensionFromFilename = (filename: string): string | null => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return null;
  return filename.substring(lastDotIndex).toLowerCase();
};

const removeFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return filename;
  return filename.substring(0, lastDotIndex);
};

const fixFilename = (filename: string, correctExtension: string): string => {
  const baseFilename = removeFileExtension(filename);
  return baseFilename + correctExtension;
};

// --- 全局 Store ---
let tasks: Map<string, DownloadTask> = new Map();
const listeners: Set<() => void> = new Set();

const setTasks = (newTasks: Map<string, DownloadTask>) => {
  tasks = newTasks;
  listeners.forEach((listener) => listener());
};

// --- React Hook ---
export function useDownload() {
  const [localTasks, setLocalTasks] = useState(tasks);

  useEffect(() => {
    const listener = () => {
      setLocalTasks(new Map(tasks));
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const addTask = (task: DownloadTask) => {
    const newTasks = new Map(tasks).set(task.id, task);
    setTasks(newTasks);
  };

  const updateTask = (id: string, updates: Partial<DownloadTask>) => {
    const newTasks = new Map(tasks);
    const existingTask = newTasks.get(id);
    if (existingTask) {
      newTasks.set(id, { ...existingTask, ...updates });
      setTasks(newTasks);
    }
  };

  const removeTask = (id: string) => {
    const newTasks = new Map(tasks);
    const taskToRemove = newTasks.get(id);
    if (taskToRemove?.blobUrl) {
      window.URL.revokeObjectURL(taskToRemove.blobUrl);
    }
    newTasks.delete(id);
    setTasks(newTasks);
  };

  const removeAllTasks = () => {
    tasks.forEach((task) => {
      if (task.blobUrl) {
        window.URL.revokeObjectURL(task.blobUrl);
      }
    });
    setTasks(new Map());
  };

  const download = async (url: string, filename: string, imageUrl?: string) => {
    const id = uuidv4();
    addTask({
      id,
      filename,
      total: 0,
      loaded: 0,
      percent: 0,
      done: false,
      imageUrl,
    });

    try {
      const response = await fetch(toStaticProxyUrl(url));
      if (!response.ok)
        throw new Error(`Request failed: ${response.statusText}`);
      if (!response.body) throw new Error("Response body is not available");

      const contentLength = response.headers.get("content-length");
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      updateTask(id, { total });

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      const read = async (): Promise<void> => {
        const { done, value } = await reader.read();
        if (done) {
          const blob = new Blob(chunks);

          // 文件类型检测和修正
          let finalFilename = filename;
          const fileBuffer = new Uint8Array(await blob.arrayBuffer());
          const detectedExtension = getFileTypeFromBuffer(fileBuffer, filename);
          const currentExtension = getFileExtensionFromFilename(filename);
          const urlExtension = getFileTypeFromUrl(url);

          // 如果检测到真实文件类型，且与当前文件名扩展名不匹配
          if (detectedExtension && currentExtension && detectedExtension !== currentExtension) {
            console.warn(`文件类型不匹配: 文件名显示为 ${currentExtension}，但实际类型为 ${detectedExtension}`);
            finalFilename = fixFilename(filename, detectedExtension);
            console.log(`文件名已修正: ${filename} -> ${finalFilename}`);
          }
          // 如果文件名没有扩展名，但检测到了文件类型
          else if (!currentExtension && detectedExtension) {
            finalFilename = filename + detectedExtension;
            console.log(`为文件添加扩展名: ${filename} -> ${finalFilename}`);
          }
          // 如果无法从文件内容检测类型，但URL中有扩展名，且文件名没有扩展名
          else if (!currentExtension && urlExtension) {
            finalFilename = filename + urlExtension;
            console.log(`从URL添加扩展名: ${filename} -> ${finalFilename}`);
          }

          const blobUrl = window.URL.createObjectURL(blob);

          updateTask(id, {
            done: true,
            percent: 100,
            loaded: total,
            blobUrl,
            filename: finalFilename // 更新任务中的文件名
          });

          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = finalFilename; // 使用修正后的文件名
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }

        chunks.push(value);
        loaded += value.length;
        const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
        updateTask(id, { loaded, percent });

        return read();
      };

      await read();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      updateTask(id, { error: message, done: true });
    }
  };

  // 获取文件大小的函数
  const getFileSize = async (url: string): Promise<number> => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      if (!response.ok) {
        // 如果HEAD请求失败，尝试GET请求获取Content-Length
        const getResponse = await fetch(url);
        const contentLength = getResponse.headers.get('content-length');
        return contentLength ? parseInt(contentLength, 10) : 0;
      }
      const contentLength = response.headers.get('content-length');
      return contentLength ? parseInt(contentLength, 10) : 0;
    } catch (error) {
      console.warn(`Failed to get file size for ${url}:`, error);
      return 0;
    }
  };

  // 批量获取视频文件大小
  const getVideosSizes = async (videos: VideoFile[]): Promise<{ url: string; size: number }[]> => {
    const sizePromises = videos.map(async (video) => ({
      url: video.url,
      size: await getFileSize(video.url)
    }));
    return Promise.all(sizePromises);
  };

  // 动态并发控制配置
  const getOptimalConcurrency = () => {
    // 基于网络连接类型和设备性能动态调整
    const connection = (navigator as any).connection;
    const cores = navigator.hardwareConcurrency || 4;

    if (connection) {
      // 根据网络类型调整
      switch (connection.effectiveType) {
        case '4g':
          return Math.min(cores, 8); // 4G网络最多8个并发
        case '3g':
          return Math.min(cores, 4); // 3G网络最多4个并发
        case '2g':
          return 2; // 2G网络最多2个并发
        default:
          return Math.min(cores, 6); // 默认6个并发
      }
    }

    // 没有网络信息时，基于CPU核心数
    return Math.min(cores, 6);
  };

  // 批量下载视频并打包成ZIP
  const downloadZip = async (
    videos: VideoFile[],
    filename: string = "videos.zip"
  ) => {
    if (videos.length === 0) {
      return;
    }
    const taskId = uuidv4();

    // 提取视频图像URL
    const imageUrls = videos
      .filter((video) => video.imageUrl)
      .map((video) => video.imageUrl);

    // 创建初始任务
    addTask({
      id: taskId,
      filename: filename,
      total: 0,
      loaded: 0,
      percent: 0,
      done: false,
      imageUrl: imageUrls.length > 0 ? imageUrls[0] : undefined,
    });

    try {
      // 1. 获取所有视频的实际大小
      updateTask(taskId, {
        filename,
        percent: 0
      });

      const videoSizes = await getVideosSizes(videos);
      const totalVideoSize = videoSizes.reduce((sum, item) => sum + item.size, 0);

      // 更新任务，显示实际总大小
      updateTask(taskId, {
        filename: filename,
        total: totalVideoSize,
        percent: 0
      });

      const zip = new JSZip();
      const totalVideos = videos.length;
      let completedVideos = 0;
      let totalDownloadedBytes = 0;

      // 动态并发控制
      const concurrencyLimit = getOptimalConcurrency();
      console.log(`使用 ${concurrencyLimit} 个并发下载`);

      // 进度更新节流控制
      let lastUpdateTime = 0;
      const updateThrottle = 100; // 100ms更新一次

      const updateProgress = (force = false) => {
        const now = Date.now();
        if (force || now - lastUpdateTime > updateThrottle) {
          const downloadProgress = totalVideoSize > 0
            ? Math.round((totalDownloadedBytes / totalVideoSize) * 85) + 10
            : Math.round((completedVideos / totalVideos) * 85) + 10;

          updateTask(taskId, {
            loaded: totalDownloadedBytes,
            percent: Math.min(downloadProgress, 95),
          });
          lastUpdateTime = now;
        }
      };

      for (let i = 0; i < videos.length; i += concurrencyLimit) {
        const batch = videos.slice(i, i + concurrencyLimit);

        const batchPromise = Promise.all(
          batch.map(async (video, index) => {
            // 使用流式下载以获得更丝滑的进度
            const response = await fetch(video.url);
            if (!response.ok) {
              throw new Error(`Failed to download ${video.filename}: ${response.statusText}`);
            }

            if (!response.body) {
              throw new Error(`Response body is not available for ${video.filename}`);
            }

            const reader = response.body.getReader();
            const chunks: Uint8Array[] = [];
            let videoDownloadedBytes = 0;

            const read = async (): Promise<Uint8Array> => {
              const { done, value } = await reader.read();
              if (done) {
                const fileData = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
                let offset = 0;
                for (const chunk of chunks) {
                  fileData.set(chunk, offset);
                  offset += chunk.length;
                }
                return fileData;
              }

              chunks.push(value);
              videoDownloadedBytes += value.length;
              totalDownloadedBytes += value.length;

              // 更丝滑的进度更新
              updateProgress();

              return read();
            };

            const fileData = await read();

            // 文件类型检测和文件名修正
            let finalFilename = video.filename;
            const detectedExtension = getFileTypeFromBuffer(fileData, video.filename);
            const currentExtension = getFileExtensionFromFilename(video.filename);
            const urlExtension = getFileTypeFromUrl(video.url);

            // 如果检测到真实文件类型，且与当前文件名扩展名不匹配
            if (detectedExtension && currentExtension && detectedExtension !== currentExtension) {
              console.warn(`批量下载文件类型不匹配: ${video.filename} 显示为 ${currentExtension}，但实际类型为 ${detectedExtension}`);
              finalFilename = fixFilename(video.filename, detectedExtension);
              console.log(`批量下载文件名已修正: ${video.filename} -> ${finalFilename}`);
            }
            // 如果文件名没有扩展名，但检测到了文件类型
            else if (!currentExtension && detectedExtension) {
              finalFilename = video.filename + detectedExtension;
              console.log(`批量下载为文件添加扩展名: ${video.filename} -> ${finalFilename}`);
            }
            // 如果无法从文件内容检测类型，但URL中有扩展名，且文件名没有扩展名
            else if (!currentExtension && urlExtension) {
              finalFilename = video.filename + urlExtension;
              console.log(`批量下载从URL添加扩展名: ${video.filename} -> ${finalFilename}`);
            }

            zip.file(finalFilename, fileData); // 使用修正后的文件名
            completedVideos++;

            // 强制更新进度（视频完成时）
            updateProgress(true);
          })
        );

        await batchPromise;
      }

      // ZIP压缩占5%，添加压缩进度监听
      updateTask(taskId, {
        percent: 95,
        compressionProgress: 0, // 开始压缩
      });

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6 // 平衡压缩率和速度
        }
      }, (metadata) => {
        // ZIP压缩进度回调
        if (metadata.percent) {
          // 压缩进度占总进度的5%（95% -> 100%）
          const compressionProgress = Math.round(95 + (metadata.percent / 100) * 5);
          updateTask(taskId, {
            percent: Math.min(compressionProgress, 99), // 最大99%，完成时设为100%
            compressionProgress: Math.round(metadata.percent), // 存储压缩进度
          });
        }
      });

      const blobUrl = window.URL.createObjectURL(zipBlob);

      updateTask(taskId, {
        done: true,
        percent: 100,
        loaded: totalVideoSize,
        compressionProgress: undefined, // 清除压缩进度
        total: totalVideoSize,
        blobUrl,
        filename: filename,
      });

      // 自动下载
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      updateTask(taskId, { error: message, done: true });
    }
  };

  // 批量获取文件大小
  const getFilesSizes = async (files: FileItem[]): Promise<{ url: string; size: number }[]> => {
    const sizePromises = files.map(async (file) => ({
      url: file.url,
      size: await getFileSize(file.url)
    }));
    return Promise.all(sizePromises);
  };


  /**
   * 批量下载文件到ZIP
   * @param input 文件列表或文件夹映射
   * @param filename 下载的ZIP文件名
   * @description 支持多目录下载，具体使用可参考 useDownloadMaterialFolder 下载素材库目录
   */
  const downloadFileToZip = async (
    input: FileItem[] | FolderFileMap,
    filename: string = "files.zip"
  ) => {

    const files: Array<FileItem & { __folderName?: string }> = Array.isArray(input)
      ? input
      : Object.entries(input).flatMap(([folderName, list]) =>
        list.map(file => ({
          ...file,
          __folderName: folderName,
        }))
      );

    if (files.length === 0) return;

    // 静态代理
    files.forEach(file => {
      file.url = toStaticProxyUrl(file.url);
    });

    const taskId = uuidv4();

    const imageUrls = files
      .filter(file => file.imageUrl)
      .map(file => file.imageUrl!);

    addTask({
      id: taskId,
      filename,
      total: 0,
      loaded: 0,
      percent: 0,
      done: false,
      imageUrl: imageUrls[0],
    });

    try {
      // 文件大小统计
      const fileSizes = await getFilesSizes(files);
      const totalFileSize = fileSizes.reduce((sum, f) => sum + f.size, 0);

      updateTask(taskId, {
        total: totalFileSize,
        percent: 0,
      });

      const zip = new JSZip();
      let totalDownloadedBytes = 0;

      const concurrencyLimit = getOptimalConcurrency();

      let lastUpdateTime = 0;
      const updateThrottle = 100;

      const updateProgress = (force = false) => {
        const now = Date.now();
        if (force || now - lastUpdateTime > updateThrottle) {
          const percent = totalFileSize
            ? Math.round((totalDownloadedBytes / totalFileSize) * 85) + 10
            : 10;

          updateTask(taskId, {
            loaded: totalDownloadedBytes,
            percent: Math.min(percent, 95),
          });

          lastUpdateTime = now;
        }
      };

      // 并发下载
      for (let i = 0; i < files.length; i += concurrencyLimit) {
        const batch = files.slice(i, i + concurrencyLimit);

        await Promise.all(
          batch.map(async (file) => {
            const response = await fetch(file.url);
            if (!response.ok || !response.body) {
              throw new Error(`Failed to download ${file.filename}`);
            }

            const reader = response.body.getReader();
            const chunks: Uint8Array[] = [];

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              chunks.push(value);
              totalDownloadedBytes += value.length;
              updateProgress();
            }

            const fileData = new Uint8Array(
              chunks.reduce((sum, c) => sum + c.length, 0)
            );

            let offset = 0;
            for (const chunk of chunks) {
              fileData.set(chunk, offset);
              offset += chunk.length;
            }

            /** 文件名修正 */
            let finalFilename = file.filename;
            const detectedExtension = getFileTypeFromBuffer(fileData, file.filename);
            const currentExtension = getFileExtensionFromFilename(file.filename);
            const urlExtension = getFileTypeFromUrl(file.url);

            if (detectedExtension && currentExtension !== detectedExtension) {
              finalFilename = fixFilename(file.filename, detectedExtension);
            } else if (!currentExtension && detectedExtension) {
              finalFilename += detectedExtension;
            } else if (!currentExtension && urlExtension) {
              finalFilename += urlExtension;
            }

            // ZIP 写入（新增目录支持）
            if (file.__folderName) {
              zip.folder(file.__folderName)!.file(finalFilename, fileData);
            } else {
              zip.file(finalFilename, fileData);
            }

            updateProgress(true);
          })
        );
      }

      updateTask(taskId, { percent: 95 });

      const zipBlob = await zip.generateAsync(
        { type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } },
        (meta) => {
          if (meta.percent) {
            updateTask(taskId, {
              percent: Math.min(95 + Math.round(meta.percent * 0.05), 99),
            });
          }
        }
      );

      const blobUrl = URL.createObjectURL(zipBlob);

      updateTask(taskId, {
        done: true,
        percent: 100,
        loaded: totalFileSize,
        blobUrl,
      });

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
    } catch (error) {
      updateTask(taskId, {
        done: true,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };


  return {
    tasks: Array.from(localTasks.values()),
    download,
    downloadZip,
    downloadFileToZip,
    removeTask,
    removeAllTasks,
  };
}
